
import { Component, Vue } from 'vue-property-decorator';
import { dispatchGetAccountInfo, dispatchUpdateAccountInfo } from '@/store/user/actions';
import { IAccountInfo } from '@/store/user';
import { growth_phases, industries, industry_details } from '@/utils/const';
import { readHasAdminAccess } from '@/store/main/getters';
import { readCompanyId } from '@/store/admin/getters';
import { dispatchAdminGetAccountInfo } from '@/store/admin/actions';
import { dispatchGetUserCompany } from '@/store/main/actions';

@Component
export default class AccountInfo extends Vue {
  public valid = true;
  public isEdit = false;
  // 法人名
  public company_name: string | null = null;
  // 会社HP（URL）
  public company_url: string | null = null;
  // 設立年月日
  public company_foundation_date: Date | string | null = null;
  public company_foundation_year: number = 0;
  public company_foundation_month: number = 0;
  public company_foundation_day: number = 0;
  // 業種
  public display_industry_categories: string[] = [];
  public company_industry_category1: string = '-';
  public company_industry_detail1: string | null = null;
  public company_industry_category2: string = '-';
  public company_industry_detail2: string | null = null;
  public company_industry_category3: string = '-';
  public company_industry_detail3: string | null = null;
  // 事業フェーズ
  public company_growth_phase: string | null = null;
  // 担当者指名
  public user_last_name: string | null = null;
  public user_first_name: string | null = null;
  // 担当者肩書
  public user_title: string | null = null;
  // メールアドレス
  public user_email: string | null = null;

  get getIndustries() {
    return industries;
  }

  public getDetail(category: string | null) {
    if (!['', '-'].includes(this.company_industry_category1)) {
      if (!industry_details[this.company_industry_category1].includes(this.company_industry_detail1)) {
        this.company_industry_detail1 = null;
      }
    } else {
      this.company_industry_category1 = '-';
      this.company_industry_detail1 = null;
      this.company_industry_category2 = '-';
      this.company_industry_detail2 = null;
      this.company_industry_category3 = '-';
      this.company_industry_detail3 = null;
    }
    if (!['', '-'].includes(this.company_industry_category2)) {
      if (!industry_details[this.company_industry_category2].includes(this.company_industry_detail2)) {
        this.company_industry_detail2 = null;
      }
    } else {
      this.company_industry_category2 = '-';
      this.company_industry_detail2 = null;
      this.company_industry_category3 = '-';
      this.company_industry_detail3 = null;
    }
    if (!['', '-'].includes(this.company_industry_category3)) {
      if (!industry_details[this.company_industry_category3].includes(this.company_industry_detail3)) {
        this.company_industry_detail3 = null;
      }
    } else {
      this.company_industry_category3 = '-';
      this.company_industry_detail3 = null;
    }
    return (category)
      ? industry_details[category]
      : null;
  }

  public checkGrowthPhase() {
    if (!this.company_growth_phase) {
      this.company_growth_phase = '-';
    }
  }

  get getGrowthPhases() {
    return growth_phases;
  }

  get checkAdminAccess() {
    return readHasAdminAccess(this.$store);
  }

  public async mounted() {
    this.reset();
  }

  public async reset() {
    this.isEdit = false;
    this.display_industry_categories = [];
    let accountInfo: IAccountInfo | null = null;

    // 管理者ユーザー処理
    if (readHasAdminAccess(this.$store)) {
      const company_id = readCompanyId(this.$store);
      if (company_id == null) {
        this.$router.push('/main/admin');
      } else {
        accountInfo = await dispatchAdminGetAccountInfo(this.$store, company_id);
      }
      // 一般ユーザー処理
    } else {
      accountInfo = await dispatchGetAccountInfo(this.$store);
    }

    if (accountInfo) {
      if (accountInfo.company_name) {
        this.company_name = accountInfo.company_name;
      }
      if (accountInfo.company_url) {
        this.company_url = accountInfo.company_url;
      }
      if (accountInfo.company_foundation_date) {
        let date = new Date(accountInfo.company_foundation_date);
        this.company_foundation_year = date.getFullYear();
        this.company_foundation_month = date.getMonth() + 1;
        this.company_foundation_day = date.getDate();
      }
      if (accountInfo.company_industry_category1) {
        this.company_industry_category1 = this.extractIndustry('text', accountInfo.company_industry_category1)!;
        this.company_industry_detail1 = accountInfo.company_industry_detail1;
        this.display_industry_categories.push(accountInfo.company_industry_category1 + '／' + accountInfo.company_industry_detail1);
      }
      if (accountInfo.company_industry_category2) {
        this.company_industry_category2 = this.extractIndustry('text', accountInfo.company_industry_category2)!;
        this.company_industry_detail2 = accountInfo.company_industry_detail2;
        this.display_industry_categories.push(accountInfo.company_industry_category2 + '／' + accountInfo.company_industry_detail2);
      }
      if (accountInfo.company_industry_category3) {
        this.company_industry_category3 = this.extractIndustry('text', accountInfo.company_industry_category3)!;
        this.company_industry_detail3 = accountInfo.company_industry_detail3;
        this.display_industry_categories.push(accountInfo.company_industry_category3 + '／' + accountInfo.company_industry_detail3);
      }
      if (accountInfo.company_growth_phase) {
        this.company_growth_phase = accountInfo.company_growth_phase;
      }
      if (accountInfo.user_last_name) {
        this.user_last_name = accountInfo.user_last_name;
      }
      if (accountInfo.user_first_name) {
        this.user_first_name = accountInfo.user_first_name;
      }
      if (accountInfo.user_title) {
        this.user_title = accountInfo.user_title;
      }
      if (accountInfo.user_email) {
        this.user_email = accountInfo.user_email;
      }
    }
  }


  public edit() {
    this.isEdit = true;
  }

  public async save() {
    if (await this.$validator.validateAll()) {
      this.company_foundation_date = (this.company_foundation_year && this.company_foundation_month && this.company_foundation_day)
        ? this.company_foundation_year.toString() + '-' + this.company_foundation_month.toString() + '-' + this.company_foundation_day.toString()
        : null;
      const accountInfo: IAccountInfo = {
        company_name: this.company_name,
        company_url: this.company_url,
        company_foundation_date: this.company_foundation_date,
        company_industry_category1: this.extractIndustry('value', this.company_industry_category1),
        company_industry_detail1: this.company_industry_detail1,
        company_industry_category2: this.extractIndustry('value', this.company_industry_category2),
        company_industry_detail2: this.company_industry_detail2,
        company_industry_category3: this.extractIndustry('value', this.company_industry_category3),
        company_industry_detail3: this.company_industry_detail3,
        company_growth_phase: (this.company_growth_phase) ? this.company_growth_phase : '',
        user_last_name: this.user_last_name,
        user_first_name: this.user_first_name,
        user_title: this.user_title,
        user_email: this.user_email
      };
      await dispatchUpdateAccountInfo(this.$store, accountInfo);
      await dispatchGetUserCompany(this.$store);
      this.reset();
    }
  }

  // 日付フォーマット
  public formatDate(date: Date | null, reverse: boolean = false, delimiter: string | null = null): string {
    let formatDate = '';
    if (date) {
      if (delimiter != null) {
        if (reverse) {
          formatDate = date.getDate() + delimiter + (date.getMonth() + 1) + delimiter + date.getFullYear();
        } else {
          formatDate = date.getFullYear() + delimiter + (date.getMonth() + 1) + delimiter + date.getDate();
        }
      } else {
        formatDate = date.getFullYear() + '年' + (date.getMonth() + 1) + '月' + date.getDate() + '日';
      }
    }
    return formatDate;
  }

  public extractIndustry(extracter: 'value' | 'text', param: string | null): string | null {
    let str: string | null = null;
    for (let industry of industries) {
      if (extracter == 'value' && industry.value == param && param) {
        str = industry.text;
      } else if (extracter == 'text' && industry.text == param) {
        str = industry.value;
      }
      if (str != null) break;
    }
    return str;
  }

  public resetDay() {
    this.company_foundation_day = 0;
  }

  public years() {
    const years: number[] = [];
    let year = (this.company_foundation_year < (new Date().getFullYear() - 30))
      ? this.company_foundation_year
      : (new Date().getFullYear() - 30);
    for (year; year <= (new Date().getFullYear() + 1); year++) {
      years.push(year);
    }
    return years;
  }

  public months() {
    const months = [...Array(12)].map((ele, i) => i + 1);
    return months;
  }

  public days() {
    const days: number[] = [];
    const date = (this.company_foundation_year && this.company_foundation_month)
      ? new Date(this.company_foundation_year, this.company_foundation_month, 0)
      : new Date();
    for (let day = 1; day <= date.getDate(); day++) {
      days.push(day)
    }
    return days
  }
}
